import React, { useState, ChangeEvent, ReactNode, useRef } from 'react'

import Button from '../../../scentregroup-ui/components/form/button'
import Form from '../../../scentregroup-ui/components/form/form'
import Textfield from '../../../scentregroup-ui/components/form/textfield'
import Row from '../../../scentregroup-ui/components/form/row'
import { SocialButtons } from '../social-buttons'

import { isEmail, isEmptyString } from '../../../lib/validators'
import {
  Result,
  createSuccessResult,
  createErrorResult,
} from '../../../lib/types/result'
import { createFormValidator } from '../../../lib/formValidation'
import {
  ValidateSignUpEmailError,
  ValidateSignUpEmail,
} from '../../../lib/apiService'
import useAnalytics from '../../../lib/analytics/use-analytics'
import { logError, trackContinueSignup } from '../../../lib/analytics/events'
import style from './index.scss'
import useBlurValidation from '../../../lib/formValidation/use-blur-validation'

const EMAIL_IS_EMPTY = 'Please enter a valid email address'

const CONTEXT_MIGRATE = 'migrate'

const getErrorMessages = () => ({
  EMAIL_IS_EMPTY,
  EMAIL_IS_INVALID: EMAIL_IS_EMPTY,
  EMAIL_IS_USED:
    'There is an active Westfield account for this email. Please login instead or try another one.',
  EMAIL_IS_REJECTED: 'The email is not valid. Please try another one.',
})

const validateEmail = (email: string): Result<string> => {
  if (isEmptyString(email)) {
    return createErrorResult(getErrorMessages().EMAIL_IS_EMPTY)
  }

  return isEmail(email)
    ? createSuccessResult()
    : createErrorResult(getErrorMessages().EMAIL_IS_INVALID)
}

export interface OnChangeEmail {
  (email: string): void
}

export interface Values extends Record<string, unknown> {
  email: string
}

type OnSuccessData = Values

export interface OnSuccess {
  (data: OnSuccessData): void
}

interface OnFailureData extends OnSuccessData {
  errors: ValidateSignUpEmailError[]
}

export interface OnFailure {
  (data: OnFailureData): void
}

export interface RenderLoginLink {
  (className?: string): ReactNode
}

interface Props {
  draftEmail: string
  onChangeEmail: OnChangeEmail
  onSuccess: OnSuccess
  onFailure: OnFailure
  validateSignUpEmail: ValidateSignUpEmail
  googleLogin?: () => void
  appleLogin?: () => void
}

const SignUpFormFields: React.FC<Props> = ({
  draftEmail,
  onChangeEmail,
  onSuccess,
  onFailure,
  validateSignUpEmail,
  googleLogin,
  appleLogin,
}) => {
  const [email, setEmail] = useState(draftEmail)
  const [emailError, setEmailError] = useState('')
  const [isValidatingSignUpEmail, setIsValidatingSignUpEmail] = useState(false)
  const emailInput = useRef<HTMLInputElement>(null)
  const { dataLayer } = useAnalytics()
  const {
    isBlurValidationEnabled,
    enableBlurValidation,
    disableBlurValidation,
  } = useBlurValidation()

  const params = new URLSearchParams(window.location.search)
  const contextParam = params.get('context')

  const validateForm = createFormValidator<Values>(
    { email },
    (_fieldName, errorValue) => {
      setEmailError(errorValue)
      if (errorValue) {
        dataLayer(logError('Sign Up Validation Error', errorValue))
      }
    },
    () => [
      {
        validationFn: validateEmail,
        fieldName: 'email',
        fieldElement: emailInput.current,
      },
    ]
  )

  const onSubmit = async (e: ChangeEvent<HTMLButtonElement>) => {
    e.preventDefault()

    const validationResult = validateForm()

    if (!validationResult.success) {
      return
    }

    setIsValidatingSignUpEmail(true)
    const result = await validateSignUpEmail(email)
    setIsValidatingSignUpEmail(false)

    if (result.success) {
      dataLayer(trackContinueSignup())
      return onSuccess({ email })
    }

    let formError = ''
    const systemErrors: ValidateSignUpEmailError[] = []

    for (const resultError of result.error) {
      switch (resultError) {
        case ValidateSignUpEmailError.Used:
          formError = getErrorMessages().EMAIL_IS_USED
          break
        case ValidateSignUpEmailError.Invalid:
          formError = getErrorMessages().EMAIL_IS_INVALID
          break
        case ValidateSignUpEmailError.Rejected:
          formError = getErrorMessages().EMAIL_IS_REJECTED
          break
        default:
          systemErrors.push(resultError)
      }
    }

    if (formError) {
      setEmailError(formError)
      dataLayer(logError('Sign Up Error', formError))
    }

    if (systemErrors.length > 0) {
      onFailure({ email, errors: systemErrors })
      dataLayer(logError('Sign Up Error', systemErrors.toString()))
    }
  }

  const onBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isBlurValidationEnabled) {
      return
    }

    validateForm(e.target.name)
  }

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setEmail(value)
    onChangeEmail(value)
  }

  return (
    <Form onSubmit={onSubmit} noValidate>
      <Row>
        <Textfield
          classNames={{ wrapper: style.emailField }}
          innerRef={emailInput}
          type="email"
          label="Email"
          name="email"
          id="email"
          value={email}
          inputMode="email"
          autoComplete="email"
          errorMessage={emailError}
          isHorizontal={false}
          onChange={onChange}
          onBlur={onBlur}
        />
      </Row>

      <Row>
        <div
          className={style.submitButtonWrapper}
          data-testid="submit-button-wrapper"
          onMouseEnter={disableBlurValidation}
          onMouseLeave={enableBlurValidation}
        >
          <Button
            buttonType="submit"
            loading={isValidatingSignUpEmail}
            disabled={isValidatingSignUpEmail}
          >
            Continue
          </Button>

          {contextParam !== CONTEXT_MIGRATE ? (
            <SocialButtons appleLogin={appleLogin} googleLogin={googleLogin} />
          ) : null}
        </div>
      </Row>
    </Form>
  )
}

export default SignUpFormFields
